import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import { toRgba } from '../../../../ui/theme/colors';
import Icon from '../../../../ui/Icon';

const WidgetContainer = styled(Flex)`
  background: ${(props) => {
    const color = props.$brandColor || theme.colors.PRIMARY;
    return `linear-gradient(135deg, ${toRgba(color, 0.05)} 0%, ${toRgba(color, 0.08)} 100%)`;
  }};
  border-radius: 16px;
  padding: 24px;
  border: 1px solid
    ${(props) => {
      const color = props.$brandColor || theme.colors.PRIMARY;
      return toRgba(color, 0.12);
    }};
  position: relative;
  overflow: hidden;
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const IconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${(props) => props.$brandColor || theme.colors.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StreakNumber = styled(Typo.PageHeader)`
  color: ${(props) => props.$brandColor || theme.colors.PRIMARY};
  font-size: 48px;
  line-height: 1;
  margin: 0;
`;

const StreakDaysContainer = styled(Flex)`
  gap: 6px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const DayDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => (props.$completed ? props.$brandColor : theme.colors.NEU_200)};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const StatItem = styled(Flex)`
  padding: 12px 16px;
  background: ${theme.colors.NEU_0};
  border-radius: 12px;
  flex: 1;
  min-width: 120px;
`;

/**
 * LearningStreakWidget Component
 *
 * Displays the student's learning streak information including:
 * - Current streak
 * - Longest streak
 * - Total days learned
 * - Visual representation of recent activity
 */
const LearningStreakWidget = ({ streakData }) => {
  if (!streakData) return null;

  const { currentStreak, longestStreak, totalDaysLearned, streakHistory = [] } = streakData;

  return (
    <WidgetContainer direction='column' align='flex-start' $brandColor={theme.colors.SECONDARY}>
      <Flex align='center' gap='16' width='100%' style={{ marginBottom: '20px' }}>
        <IconCircle $brandColor={theme.colors.SECONDARY}>
          <Icon.Bolt size={'medium'} color={theme.colors.NEU_0} />
        </IconCircle>
        <Flex direction='column' align='flex-start' style={{ flex: 1 }}>
          <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
            Learning Streak
          </Typo.ContentSmallHeader>
          <Typo.Info color={theme.colors.NEU_600}>Keep the momentum going!</Typo.Info>
        </Flex>
      </Flex>

      <Flex align='center' gap='12' style={{ marginBottom: '20px' }}>
        <StreakNumber $brandColor={theme.colors.SECONDARY}>{currentStreak}</StreakNumber>
        <Flex direction='column' align='flex-start'>
          <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
            Day Streak
          </Typo.MediumText>
          <Typo.Info color={theme.colors.NEU_600}>{currentStreak === 1 ? 'Keep it up!' : 'Amazing progress!'}</Typo.Info>
        </Flex>
      </Flex>

      {streakHistory.length > 0 && (
        <>
          <Typo.Info color={theme.colors.NEU_600} style={{ marginBottom: '12px' }}>
            Last 7 days
          </Typo.Info>
          <StreakDaysContainer>
            {streakHistory.slice(-7).map((day, index) => (
              <DayDot key={index} $completed={day.completed} $brandColor={theme.colors.SECONDARY} title={day.date}>
                {day.completed && <Icon.Check size={'x-small'} color={theme.colors.NEU_0} />}
              </DayDot>
            ))}
          </StreakDaysContainer>
        </>
      )}

      <Flex gap='12' width='100%' style={{ marginTop: '20px' }} wrap='wrap'>
        <StatItem direction='column' align='center'>
          <Typo.ContentSmallHeader color={theme.colors.SECONDARY}>{longestStreak}</Typo.ContentSmallHeader>
          <Typo.Info color={theme.colors.NEU_600}>Longest Streak</Typo.Info>
        </StatItem>
        <StatItem direction='column' align='center'>
          <Typo.ContentSmallHeader color={theme.colors.SECONDARY}>{totalDaysLearned}</Typo.ContentSmallHeader>
          <Typo.Info color={theme.colors.NEU_600}>Total Days</Typo.Info>
        </StatItem>
      </Flex>
    </WidgetContainer>
  );
};

export default LearningStreakWidget;
